import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { Visibility, Delete, Close, MarkEmailRead } from '@mui/icons-material';
import { contactPageService } from '../../services/firebase';

const ManageMessagesPage: React.FC = () => {
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const data = await contactPageService.getContactMessages();
      setMessages(data);
    } catch (err) {
      setError('Mesajlar yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (message: any) => {
    setSelectedMessage(message);
    setDialogOpen(true);
    if (!message.isRead) {
      handleMarkAsRead(message.id);
    }
  };

  const handleMarkAsRead = async (messageId: string) => {
    try {
      await contactPageService.markMessageAsRead(messageId);
      fetchMessages(); // Mesajları yenile
    } catch (err) {
      setError('Mesaj okundu olarak işaretlenirken hata oluştu.');
    }
  };

  const handleDelete = async (messageId: string) => {
    if (window.confirm('Bu mesajı silmek istediğinize emin misiniz?')) {
      try {
        await contactPageService.deleteContactMessage(messageId);
        fetchMessages(); // Mesajları yenile
      } catch (err) {
        setError('Mesaj silinirken hata oluştu.');
      }
    }
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return 'Tarih bilinmiyor';
    
    try {
      let date: Date;
      
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'Geçersiz tarih';
      }
      
      return date.toLocaleDateString('tr-TR');
    } catch (error) {
      console.error('Date formatting error:', error, timestamp);
      return 'Tarih hatası';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={4}>
        İletişim Mesajları Yönetimi
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card sx={{ borderRadius: 3, boxShadow: 'var(--shadow-md)' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
            <Typography variant="h6" fontWeight={600}>
              Gelen Mesajlar ({messages.length})
            </Typography>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">Henüz mesaj yok.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {messages.map((message, index) => (
                <ListItem
                  key={message.id}
                  alignItems="flex-start"
                  sx={{
                    borderBottom: index < messages.length - 1 ? '1px solid #f0f0f0' : 'none',
                    py: 2,
                    px: 3,
                    backgroundColor: message.isRead ? 'inherit' : 'rgba(25, 118, 210, 0.04)',
                    borderLeft: message.isRead ? 'none' : '4px solid #1976d2'
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 2 }}>
                        {message.name}
                      </Typography>
                      {!message.isRead && (
                        <Chip label="Yeni" size="small" color="primary" sx={{ fontSize: '0.75rem' }} />
                      )}
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {message.email} | {message.phone}
                    </Typography>
                    
                    <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                      Konu: {message.subject}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }} noWrap>
                      {message.message}
                    </Typography>
                    
                    <Typography variant="caption" color="text.secondary">
                      {formatTimestamp(message.createdAt)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => handleView(message)}
                      variant="outlined"
                    >
                      Görüntüle
                    </Button>
                    
                    {!message.isRead && (
                      <Button
                        size="small"
                        startIcon={<MarkEmailRead />}
                        onClick={() => handleMarkAsRead(message.id)}
                        variant="outlined"
                        color="info"
                      >
                        Okundu
                      </Button>
                    )}
                    
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(message.id)}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Mesaj Detay Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Mesaj Detayı
          <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedMessage && (
            <Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {selectedMessage.name}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>E-posta:</strong> {selectedMessage.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Telefon:</strong> {selectedMessage.phone}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Tarih:</strong> {formatTimestamp(selectedMessage.createdAt)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" fontWeight={500} sx={{ mb: 1 }}>
                Konu: {selectedMessage.subject}
              </Typography>
              
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {selectedMessage.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} variant="contained">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ManageMessagesPage; 